import {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MapRef } from "react-map-gl";
import type { BusinessTypeFilterSelection } from "../utils/businessTypeFilters";

type TripperWidgetContextValue = {
  atlasMode: boolean;
  forceDesktop: boolean;
  verticalStack: boolean;
  businessTypeFiltersEnabled: boolean;
  businessTypeFilterSelection: BusinessTypeFilterSelection;
  setBusinessTypeFilterSelection: (selection: BusinessTypeFilterSelection) => void;
  isAutoFollowActive: boolean;
  setIsAutoFollowActive: (isActive: boolean) => void;
  mapRef: MutableRefObject<MapRef | null>;
  /** Bumped when the map instance finishes its initial bounds fit. */
  mapInstanceVersion: number;
  notifyMapInstanceReady: () => void;
  /**
   * Ref to the accent-color div in TripperWidgetProviders.
   * TripperWidget writes CSS custom properties here after data loads
   * so the itinerary's primaryColor can serve as a fallback when no
   * explicit widget-level override was set.  Using a ref avoids a
   * state change in the provider (which would remount the Shadow DOM).
   */
  accentDivRef: MutableRefObject<HTMLDivElement | null>;
};

type TripperScrollContextValue = {
  activeItemId: string | null;
  setActiveItemId: (itemId: string | null) => void;
  activeSegmentId: string | null;
  setActiveSegmentId: (segmentId: string | null) => void;
  activeStopGroupId: string | null;
  setActiveStopGroupId: (stopGroupId: string | null) => void;
  isMetadataInView: boolean;
  setIsMetadataInView: (isInView: boolean) => void;
};

type TripperScrollProgressContextValue = {
  /** Per-segment scroll progress (0..1), keyed by segment ID */
  segmentScrollProgress: Record<string, number>;
  setSegmentScrollProgress: (progress: Record<string, number>) => void;
};

const TripperWidgetContext = createContext<TripperWidgetContextValue | null>(null);
const TripperScrollContext = createContext<TripperScrollContextValue | null>(null);
const TripperScrollProgressContext = createContext<TripperScrollProgressContextValue | null>(null);

type TripperWidgetContextProviderProps = PropsWithChildren & {
  mapRef: MutableRefObject<MapRef | null>;
  accentDivRef: MutableRefObject<HTMLDivElement | null>;
  atlasMode?: boolean;
  forceDesktop?: boolean;
  verticalStack?: boolean;
  businessTypeFiltersEnabled?: boolean;
};

export const TripperWidgetContextProvider = ({
  children,
  mapRef,
  accentDivRef,
  atlasMode = false,
  forceDesktop = false,
  verticalStack = false,
  businessTypeFiltersEnabled = false,
}: TripperWidgetContextProviderProps) => {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [activeStopGroupId, setActiveStopGroupId] = useState<string | null>(null);
  const [isAutoFollowActive, setIsAutoFollowActive] = useState(!atlasMode);
  const [isMetadataInView, setIsMetadataInView] = useState(true);
  const [segmentScrollProgress, setSegmentScrollProgress] = useState<Record<string, number>>({});
  const [mapInstanceVersion, setMapInstanceVersion] = useState(0);
  const [businessTypeFilterSelection, setBusinessTypeFilterSelection] =
    useState<BusinessTypeFilterSelection>(null);

  useEffect(() => {
    setBusinessTypeFilterSelection(null);
  }, [businessTypeFiltersEnabled]);

  useEffect(() => {
    if (atlasMode) {
      setIsAutoFollowActive(false);
    }
  }, [atlasMode]);

  const notifyMapInstanceReady = useCallback(() => {
    setMapInstanceVersion(version => version + 1);
  }, []);

  const widgetValue = useMemo<TripperWidgetContextValue>(
    () => ({
      atlasMode,
      forceDesktop,
      verticalStack,
      businessTypeFiltersEnabled,
      businessTypeFilterSelection,
      setBusinessTypeFilterSelection,
      isAutoFollowActive,
      setIsAutoFollowActive,
      mapRef,
      mapInstanceVersion,
      notifyMapInstanceReady,
      accentDivRef,
    }),
    [atlasMode, forceDesktop, verticalStack, businessTypeFiltersEnabled, businessTypeFilterSelection, isAutoFollowActive, mapRef, mapInstanceVersion, notifyMapInstanceReady, accentDivRef],
  );

  const scrollValue = useMemo<TripperScrollContextValue>(
    () => ({
      activeItemId,
      setActiveItemId,
      activeSegmentId,
      setActiveSegmentId,
      activeStopGroupId,
      setActiveStopGroupId,
      isMetadataInView,
      setIsMetadataInView,
    }),
    [
      activeItemId,
      activeSegmentId,
      activeStopGroupId,
      isMetadataInView,
    ],
  );

  const progressValue = useMemo<TripperScrollProgressContextValue>(
    () => ({
      segmentScrollProgress,
      setSegmentScrollProgress,
    }),
    [segmentScrollProgress],
  );

  return (
    <TripperWidgetContext.Provider value={widgetValue}>
      <TripperScrollContext.Provider value={scrollValue}>
        <TripperScrollProgressContext.Provider value={progressValue}>
          {children}
        </TripperScrollProgressContext.Provider>
      </TripperScrollContext.Provider>
    </TripperWidgetContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTripperWidgetContext = () => {
  const context = useContext(TripperWidgetContext);
  if (!context) {
    throw new Error("useTripperWidgetContext must be used within TripperWidgetContextProvider");
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTripperScrollContext = () => {
  const context = useContext(TripperScrollContext);
  if (!context) {
    throw new Error("useTripperScrollContext must be used within TripperWidgetContextProvider");
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTripperScrollProgressContext = () => {
  const context = useContext(TripperScrollProgressContext);
  if (!context) {
    throw new Error("useTripperScrollProgressContext must be used within TripperWidgetContextProvider");
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAtlasMode = () => useTripperWidgetContext().atlasMode;

// eslint-disable-next-line react-refresh/only-export-components
export const useForceDesktop = () => useTripperWidgetContext().forceDesktop;

// eslint-disable-next-line react-refresh/only-export-components
export const useVerticalStack = () => useTripperWidgetContext().verticalStack;
